import React, { createContext, useContext } from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import Checkbox from '../Checkbox';
import stylesheet from './Table.css';
import { SelectionMode } from './useDataTableState';

const TableHeadContext = createContext('');

export type TableElementProps = {
  children?: React.ReactNode;
};

export type TableRowProps = {
  /** Unique identifier for the row */
  id?: string | number;
  /** Callback function triggered on row click */
  onClick?: (
    id: string | number,
    selected: boolean,
    selectionMode: SelectionMode,
  ) => void;
  /** Type of the row */
  scope?: 'heading' | 'body';
  /** Is row selectable to decide whether to show checkbox */
  isSelectable?: boolean;
  /** Indicates if the row is selected */
  selected?: boolean;
  /** Indicates if the row is in an indeterminate state for checkbox */
  indeterminate?: boolean;
} & TableElementProps;

export type TableProps = {
  /** caption for the table */
  caption: React.ReactNode;
  /** Additional class names for the table */
  className?: string;
  /** Whether the first column should be fixed */
  fixedFirstColumn?: boolean;
  /** Whether the caption should be hidden */
  hideCaption?: boolean;
  /** Whether the table should have striped rows */
  isStriped?: boolean;
  /** Whether the table header should be sticky */
  stickyHeader?: boolean;
  /** Whether the table should take full width of the parent */
  isFullWidth?: boolean;
} & TableElementProps;

export type TableCellProps = {
  /** Whether the cell is a header or body cell */
  scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
  /** Additional classnames for the cell */
  className?: string;
  /** How many columns should this cell span over */
  colSpan?: number;
} & TableElementProps;

const Table = React.forwardRef<HTMLTableElement, TableProps>(function Table(
  {
    className,
    children,
    caption,
    hideCaption = false,
    isStriped = false,
    fixedFirstColumn = false,
    stickyHeader = false,
    isFullWidth = false,
  },
  forwardedRef,
) {
  const classList = cx(stylesheet.root, className, {
    [stylesheet.striped]: isStriped,
    [stylesheet.fixedFirstColumn]: fixedFirstColumn,
    [stylesheet.stickyHeader]: stickyHeader,
    [stylesheet.fullWidth]: isFullWidth,
  });
  return (
    <table ref={forwardedRef} className={classList}>
      <caption className={cx(hideCaption && 'wink-visually-hidden')}>
        {caption}
      </caption>
      {children}
    </table>
  );
});

const TableHead = ({ children }: TableElementProps) => {
  return (
    <TableHeadContext.Provider value="tHead">
      <thead>{children}</thead>
    </TableHeadContext.Provider>
  );
};

const TableBody = ({ children }: TableElementProps) => {
  return <tbody>{children}</tbody>;
};

const TableCell = ({ children, scope, className, colSpan }: TableCellProps) => {
  const tHead = useContext(TableHeadContext);
  /** Identify which element to use depending on the parent wrapper of <thead></thead> or <tbody></tbody>,
   * as well as whether or not the cell is a row header. */
  const Cell = tHead || scope ? 'th' : 'td';

  const isRedesigned = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  return (
    <Cell
      className={
        isRedesigned
          ? cx(
              {
                [stylesheet.headingCell]: Cell === 'th',
                [stylesheet.bodyCell]: Cell === 'td',
              },
              className,
            )
          : className
      }
      scope={scope}
      {...(isRedesigned && { colSpan })}
    >
      {children}
    </Cell>
  );
};

const TableRow = ({
  children,
  id = '',
  scope,
  isSelectable = false,
  selected = false,
  indeterminate = false,
  onClick = undefined,
}: TableRowProps) => {
  const tHead = useContext(TableHeadContext);
  const rowType = tHead || scope ? 'heading' : 'body';

  const isRedesigned = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const handleRowClick = (e: React.MouseEvent) => {
    if (isSelectable && rowType === 'body') {
      const target = e.target as HTMLElement;
      if (e.target && ['TD', 'DIV'].includes(target.tagName)) {
        if (onClick) {
          onClick(id!, !selected, SelectionMode.Single);
        }
      }
    }
  };

  if (isRedesigned) {
    return (
      <tr
        id={String(id)}
        onClick={handleRowClick}
        className={cx({
          [stylesheet.selectedRow]:
            (isRedesigned && rowType === 'heading' && indeterminate) ||
            selected,
          [stylesheet.selectableRow]:
            isRedesigned && isSelectable && rowType === 'body',
        })}
      >
        {isRedesigned && isSelectable && (
          <TableCell>
            <Checkbox
              indeterminate={indeterminate}
              onChange={() =>
                onClick && onClick(id!, !selected, SelectionMode.Single)
              }
              value={selected}
              aria-labelledby="row-selectable"
              hideLabel
            />
          </TableCell>
        )}
        {children}
      </tr>
    );
  }
  return <tr>{children}</tr>;
};

export { Table as default, TableHead, TableBody, TableRow, TableCell };
